import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { X, Sparkles, ChevronDown, ChevronUp, Battery, BatteryLow, BatteryMedium, BatteryFull } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
    { id: 'great', emoji: '😄', label_de: 'Super', label_en: 'Great', color: 'from-green-400 to-emerald-500' },
    { id: 'good', emoji: '🙂', label_de: 'Gut', label_en: 'Good', color: 'from-cyan-400 to-blue-500' },
    { id: 'okay', emoji: '😐', label_de: 'Okay', label_en: 'Okay', color: 'from-yellow-400 to-amber-500' },
    { id: 'low', emoji: '😔', label_de: 'Mäßig', label_en: 'Low', color: 'from-orange-400 to-red-400' },
    { id: 'bad', emoji: '😢', label_de: 'Schlecht', label_en: 'Bad', color: 'from-red-400 to-rose-600' },
];

const ENERGY_LEVELS = [
    { id: 'high', icon: BatteryFull, label_de: 'Viel Energie', label_en: 'High Energy', color: 'text-green-500' },
    { id: 'medium', icon: BatteryMedium, label_de: 'Mittel', label_en: 'Medium', color: 'text-yellow-500' },
    { id: 'low', icon: BatteryLow, label_de: 'Wenig Energie', label_en: 'Low Energy', color: 'text-red-500' },
];

// Nanouk images
const NANOUK_ASKING = "https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop";
const NANOUK_HAPPY = "https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop";

export function MoodTracker({ onMoodLogged }) {
    const { language } = useLanguage();
    const [hasLoggedToday, setHasLoggedToday] = useState(false);
    const [todayMood, setTodayMood] = useState(null);
    const [selectedMood, setSelectedMood] = useState(null);
    const [selectedEnergy, setSelectedEnergy] = useState(null);
    const [note, setNote] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [nanoukResponse, setNanoukResponse] = useState(null);
    const [loading, setLoading] = useState(true);

    const t = {
        de: {
            question: 'Wie fühlst du dich gerade?',
            questionShort: 'Wie geht\'s dir?',
            energy: 'Energie-Level',
            addNote: 'Notiz hinzufügen (optional)',
            notePlaceholder: 'Was beschäftigt dich heute?',
            submit: 'Speichern',
            thanks: 'Danke fürs Teilen!',
            alreadyLogged: 'Heute schon geloggt',
            logAgain: 'Nochmal loggen'
        },
        en: {
            question: 'How are you feeling right now?',
            questionShort: 'How are you?',
            energy: 'Energy Level',
            addNote: 'Add a note (optional)',
            notePlaceholder: 'What\'s on your mind today?',
            submit: 'Save',
            thanks: 'Thanks for sharing!',
            alreadyLogged: 'Already logged today',
            logAgain: 'Log again'
        }
    }[language];

    useEffect(() => {
        checkTodayMood();
    }, []);

    const checkTodayMood = async () => {
        try {
            const response = await axios.get(`${API}/api/mood/today`);
            if (response.data.has_logged) {
                setHasLoggedToday(true);
                setTodayMood(response.data.mood);
            }
        } catch (error) {
            console.error('Failed to check mood:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMoodSelect = (mood) => {
        setSelectedMood(mood);
        setShowDetails(true);
    };

    const handleSubmit = async () => {
        if (!selectedMood) return;
        
        setSubmitting(true);
        try {
            const response = await axios.post(`${API}/api/mood?language=${language}`, {
                mood: selectedMood.id,
                energy: selectedEnergy,
                note: note || null
            });
            
            setNanoukResponse(response.data.nanouk_response);
            setHasLoggedToday(true);
            setTodayMood(selectedMood.id);
            toast.success('+3 Punkte! 🐺');
            
            // Send note to Nanouk for personal response + save to journal
            if (note && note.trim().length > 3) {
                try {
                    const coachRes = await axios.post(`${API}/api/coach/smart-comment`, {
                        activity_type: 'mood',
                        details: `Stimmung: ${selectedMood.label[language]}, Energie: ${selectedEnergy}/5. User schreibt: "${note}"`,
                        language: language
                    });
                    if (coachRes.data?.comment) {
                        setNanoukResponse(coachRes.data.comment);
                    }
                } catch(e) {}
            }
            
            if (onMoodLogged) {
                onMoodLogged(response.data);
            }
            
            // Reset after showing response
            setTimeout(() => {
                setSelectedMood(null);
                setSelectedEnergy(null);
                setNote('');
                setShowDetails(false);
                setNanoukResponse(null);
            }, 10000);
            
        } catch (error) {
            console.error('Failed to log mood:', error);
            toast.error('Fehler beim Speichern');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogAgain = () => {
        setHasLoggedToday(false);
        setTodayMood(null);
        setNanoukResponse(null);
    };

    if (loading) {
        return null;
    }

    // Show Nanouk's response after logging
    if (nanoukResponse) {
        return (
            <Card className="overflow-hidden border-2 border-green-200 dark:border-green-800/50 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20" data-testid="mood-tracker-response">
                <CardContent className="p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-4"
                    >
                        <motion.img
                            src={NANOUK_HAPPY}
                            alt="Nanouk"
                            className="w-16 h-16 rounded-full"
                            animate={{ rotate: [0, -5, 5, 0] }}
                            transition={{ duration: 0.5 }}
                        />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                                {t.thanks}
                            </p>
                            <p className="text-sm text-green-700 dark:text-green-300">
                                {nanoukResponse}
                            </p>
                        </div>
                    </motion.div>
                </CardContent>
            </Card>
        );
    }

    // Show compact view if already logged today
    if (hasLoggedToday) {
        const loggedMood = MOODS.find(m => m.id === todayMood);
        return (
            <Card className="overflow-hidden" data-testid="mood-tracker-logged">
                <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">{loggedMood?.emoji}</span>
                            <div>
                                <p className="text-xs text-muted-foreground">{t.alreadyLogged}</p>
                                <p className="text-sm font-medium">
                                    {language === 'de' ? loggedMood?.label_de : loggedMood?.label_en}
                                </p>
                            </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleLogAgain}>
                            {t.logAgain}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="overflow-hidden border-2 border-violet-200 dark:border-violet-800/50 bg-gradient-to-br from-violet-50/50 to-pink-50/50 dark:from-violet-950/20 dark:to-pink-950/20" data-testid="mood-tracker">
            <CardContent className="p-4">
                {/* Header with Nanouk */}
                <div className="flex items-center gap-3 mb-4">
                    <motion.img
                        src={NANOUK_ASKING}
                        alt="Nanouk"
                        className="w-12 h-12 rounded-full"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                    <div className="flex-1">
                        <p className="font-medium text-violet-900 dark:text-violet-100">
                            {showDetails ? t.question : t.questionShort}
                        </p>
                        {!showDetails && (
                            <p className="text-xs text-violet-600 dark:text-violet-400">
                                {language === 'de' ? 'Tippe auf ein Emoji' : 'Tap an emoji'}
                            </p>
                        )}
                    </div>
                    {showDetails && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                                setShowDetails(false);
                                setSelectedMood(null);
                            }}
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>

                {/* Mood Selection */}
                <div className="flex justify-between gap-2 mb-4">
                    {MOODS.map((mood) => (
                        <motion.button
                            key={mood.id}
                            onClick={() => handleMoodSelect(mood)}
                            className={`flex-1 flex flex-col items-center p-2 rounded-xl transition-all ${
                                selectedMood?.id === mood.id
                                    ? `bg-gradient-to-br ${mood.color} text-foreground shadow-lg scale-110`
                                    : 'bg-white dark:bg-secondary hover:bg-gray-50 dark:hover:bg-secondary shadow-sm'
                            }`}
                            whileHover={{ scale: selectedMood?.id === mood.id ? 1.1 : 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            data-testid={`mood-${mood.id}`}
                        >
                            <span className="text-2xl mb-1">{mood.emoji}</span>
                            <span className={`text-[10px] font-medium ${
                                selectedMood?.id === mood.id ? 'text-foreground' : 'text-muted-foreground'
                            }`}>
                                {language === 'de' ? mood.label_de : mood.label_en}
                            </span>
                        </motion.button>
                    ))}
                </div>

                {/* Extended Details */}
                <AnimatePresence>
                    {showDetails && selectedMood && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-4 overflow-hidden"
                        >
                            {/* Energy Level */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">
                                    {t.energy}
                                </p>
                                <div className="flex gap-2">
                                    {ENERGY_LEVELS.map((energy) => {
                                        const Icon = energy.icon;
                                        return (
                                            <button
                                                key={energy.id}
                                                onClick={() => setSelectedEnergy(energy.id)}
                                                className={`flex-1 flex items-center justify-center gap-1 p-2 rounded-lg text-xs font-medium transition-all ${
                                                    selectedEnergy === energy.id
                                                        ? 'bg-violet-100 dark:bg-violet-900/50 border-2 border-violet-400'
                                                        : 'bg-white dark:bg-secondary border border-gray-200 dark:border-border'
                                                }`}
                                            >
                                                <Icon className={`w-4 h-4 ${energy.color}`} />
                                                <span className="hidden sm:inline">
                                                    {language === 'de' ? energy.label_de : energy.label_en}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Optional Note */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">
                                    {t.addNote}
                                </p>
                                <Textarea
                                    placeholder={t.notePlaceholder}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    className="h-16 text-sm resize-none"
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className={`w-full bg-gradient-to-r ${selectedMood.color} text-foreground`}
                                data-testid="mood-submit-btn"
                            >
                                {submitting ? (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity }}
                                    >
                                        <Sparkles className="w-4 h-4" />
                                    </motion.div>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        {t.submit}
                                    </>
                                )}
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
}
